import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { getBalanceSheet } from "../../../../Api/apiClient";
import { getEndOfMonth, transformToTreeDataAnalytical } from "../../balanceSheetUtils";
import {
  exportAnalyticalLayersToCSV,
  exportAnalyticalLayersToPDF,
} from "../Helpers/exportHelpers";

export const useAnalyticalLayersData = (texts) => {
  const toast = useToast();
  const defaultDate = getEndOfMonth(new Date().toISOString().split("T")[0]);
  const initialFiltersRef = useRef({
    date: defaultDate,
    currency: "SAR",
    period_count: 0,
    group_by: "month",
    branches: [],
    costCenters: [],
    projects: [],
    contacts: [],
  });
  const [filters, setFilters] = useState(initialFiltersRef.current);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(true);
  const [reportData, setReportData] = useState(null);

  const fetchReportData = useCallback(
    async (currentFilters) => {
      setIsLoadingReport(true);
      const nextFilters = { ...currentFilters, date: getEndOfMonth(currentFilters.date) };
      if (nextFilters.period_count === 0) {
        delete nextFilters.group_by;
      }

      try {
        const response = await getBalanceSheet(nextFilters);
        setReportData(
          response.data?.rows
            ? transformToTreeDataAnalytical(response.data, texts)
            : transformToTreeDataAnalytical({ columns: [], rows: [] }, texts)
        );
      } catch (error) {
        console.error("Failed to fetch analytical layers:", error);
        toast({
          title: texts.reports?.error || "Error",
          description:
            error.response?.data?.errors?.[0]?.detail ||
            texts.reports?.couldNotLoadReport ||
            "Could not load report data.",
          status: "error",
        });
        setReportData(null);
      } finally {
        setIsLoadingReport(false);
      }
    },
    [texts, toast]
  );

  useEffect(() => {
    fetchReportData(initialFiltersRef.current);
  }, [fetchReportData]);

  const handleApplyFilters = useCallback(
    (overrides) => {
      setFilters((current) => {
        const nextFilters = { ...current, ...overrides };
        fetchReportData(nextFilters);
        return nextFilters;
      });
    },
    [fetchReportData]
  );

  const handleCurrencyChange = useCallback(
    (currency) => {
      handleApplyFilters({ currency });
    },
    [handleApplyFilters]
  );

  const handleExportReport = useCallback(
    async (format) => {
      setIsExporting(true);
      try {
        if (format === "PDF") {
          exportAnalyticalLayersToPDF(reportData, filters, toast, texts);
        } else {
          exportAnalyticalLayersToCSV(reportData, filters, toast, texts);
        }
      } finally {
        setIsExporting(false);
      }
    },
    [filters, reportData, texts, toast]
  );

  return {
    filters,
    isExporting,
    isLoadingReport,
    reportData,
    handleApplyFilters,
    handleCurrencyChange,
    handleExportReport,
  };
};
